import { State, StateContext, Store } from '@ngxs/store';
import { NgxsOnInit } from '@ngxs/store';
import { MatterMortgageFormTemplatesState } from '../matter-mortgage-form-templates.state';
import { GlobalFormsVersionsRepo } from '@repos/global-forms-versions.ts/global-forms-verisons.repo';
import { inject } from '@angular/core';
import {
	catchError,
	firstValueFrom,
	switchMap,
	tap,
	throwError,
	timeout,
} from 'rxjs';
import {
	MATTER_MORTGAGE_FORM_TEMPLATES_CURRENT_VERSIONS_STATE_TOKEN,
	MatterMortgageFormTemplatesCurrentVersionsStateModel,
} from './model';
import { GlobalFormsPdfService } from 'src/app/shared/services/pdf/global-forms-pdf.service';
import { TagsSelector } from 'src/app/shared/state/global/tags/tags.selector';
import { GenericFormsTemplatesState } from 'src/app/shared/state/generic-forms';
import { FormInstance, GlobalFormTemplate } from '@models/global-forms';

const DEFAULT_STATE: MatterMortgageFormTemplatesCurrentVersionsStateModel = {
	items: [],
	genericForms: [],
	isLoading: true,
};

@State<MatterMortgageFormTemplatesCurrentVersionsStateModel>({
	name: MATTER_MORTGAGE_FORM_TEMPLATES_CURRENT_VERSIONS_STATE_TOKEN,
	defaults: DEFAULT_STATE,
})
export class MatterMortgageFormTemplatesCurrentVersionState
	implements NgxsOnInit
{
	store = inject(Store);
	globalFormsVersionRepo = inject(GlobalFormsVersionsRepo);
	globalFormsPdfService = inject(GlobalFormsPdfService);
	matterMortgageFormTemplates$ = this.store.select(
		MatterMortgageFormTemplatesState.getMatterMortgageFormTemplates,
	);

	genericForms$ = this.store.select(
		GenericFormsTemplatesState.getGenericFormTemplates,
	);

	ngxsOnInit(
		ctx: StateContext<MatterMortgageFormTemplatesCurrentVersionsStateModel>,
	) {
		this.matterMortgageFormTemplates$ // TODO: This needs to be revisited to handle the tags not appearing
			.pipe(
				tap(() => ctx.patchState({ isLoading: true })),
				switchMap(async (formTemplates) => {
					try {
						return await this.loadFormWithVersion(formTemplates);
					} catch (error) {
						console.error('Error loading form versions', { error });
						return [];
					}
				}),
			)
			.subscribe((currentVersions) => {
				ctx.patchState({ items: currentVersions as any, isLoading: false });
			});

		this.genericForms$ // TODO: This needs to be revisited to handle the tags not appearing
			.pipe(
				tap(() => ctx.patchState({ isLoading: true })),
				switchMap(async (formTemplates) => {
					try {
						return await this.loadFormWithVersion(formTemplates);
					} catch (error) {
						console.error('Error loading form versions', { error });
						return [];
					}
				}),
			)
			.subscribe((currentVersions) => {
				ctx.patchState({
					genericForms: currentVersions as any,
					isLoading: false,
				});
			});
	}

	private async loadFormWithVersion(
		formTemplates: GlobalFormTemplate[],
	): Promise<any[]> {
		const promises = formTemplates.map(async (form) => {
			const versionPromise = firstValueFrom(
				this.globalFormsVersionRepo
					.Get$(form.currentVersion, {
						globalFormId: form.id,
					})
					.pipe(
						timeout(10000), // 10 second timeout
						catchError((err) => {
							console.error(
								`Error loading form version for form id: ${form.id}, version id: ${form.currentVersion}`,
								{ err },
							);
							return throwError(() => err);
						}),
					),
			);

			const contextPromise = Promise.race([
				this.globalFormsPdfService.evalExpression(
					'context',
					form?.context,
					this.store.selectSnapshot(TagsSelector.getTags),
					form,
					null,
				),
				new Promise((_, reject) =>
					setTimeout(
						() => reject(new Error('Context evaluation timeout')),
						2000,
					),
				),
			]);

			const [version, context] = await Promise.all([
				versionPromise,
				contextPromise,
			]);

			try {
				// Add timeout to field evaluation too
				const fields = await Promise.race([
					this.globalFormsPdfService.evaluateFields(
						Object.values(version.fieldMappings ?? {}),
						Array.isArray(context) ? context[0] : context,
					),
					new Promise((_, reject) =>
						setTimeout(
							() => reject(new Error('Field evaluation timeout')),
							2000,
						),
					),
				]);

				return {
					...form,
					storageRefPath: version.storageRefPath,
					barcodeStorageRefPath: version.barcodeStorageRefPath,
					fields,
					context: Array.isArray(context) ? context[0] : context,
				};
			} catch (error) {
				console.error(
					`Error processing form id: ${form.id}, version id: ${form.currentVersion}`,
					{ error },
				);
				return {
					...form,
					storageRefPath: version.storageRefPath,
					barcodeStorageRefPath: version.barcodeStorageRefPath,
					fields: {},
					context: Array.isArray(context) ? context[0] : context,
				};
			}
		});

		return await Promise.all(promises);
	}
}
